import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useCapability } from '../../../hooks/use-capability';
import { WS_EVENTS } from '../../../lib/websocket/events';
import { useWebSocket } from '../../../lib/websocket/WebSocketProvider';
import { useAuthStore } from '../../../stores/auth-store';

import { THREADS_KEY, UNREAD_KEY, threadKey } from './use-direct-messages';

import type {
  DirectMessageCreatedPayload,
  DirectThreadCreatedPayload,
  DirectThreadReadPayload,
  WsEventEnvelope,
} from '../../../lib/websocket/events';

/**
 * Liga os 3 WS events da mensageria interna (peer-coordination/direct-messages)
 * à invalidação React Query — o badge de não-lido, a caixa e o thread aberto
 * passam a atualizar ao vivo, sem polling.
 *
 * Montado no AppShell para o badge respirar mesmo fora de `/mensagens`.
 *
 * Os eventos chegam **per-participant** (emitToUser): o usuário só recebe os
 * eventos dos threads em que participa, e o remetente também recebe o próprio
 * `direct-message.created`. Por isso filtramos pelo `userId` do remetente/leitor:
 * a própria ação já foi invalidada pela mutação correspondente (reply/initiate/
 * markRead), então reagir aqui seria redundante.
 *
 * Gate barato: só assina quando o usuário tem `direct-message-participate`
 * (todo operador Team+). Sem a cap, o efeito retorna cedo e nada é assinado.
 * As regras de hooks são respeitadas — todos os hooks rodam incondicionalmente.
 */

export function useDirectMessagesWsSync(): void {
  const qc = useQueryClient();
  const { subscribe } = useWebSocket();
  const canParticipate = useCapability('direct-message-participate');
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);

  useEffect(() => {
    if (!canParticipate) return;

    const offMessage = subscribe<DirectMessageCreatedPayload>(
      WS_EVENTS.DIRECT_MESSAGE_CREATED,
      (envelope: WsEventEnvelope<DirectMessageCreatedPayload>) => {
        const { senderUserId, threadId } = envelope.data;
        if (senderUserId === currentUserId) return; // própria mensagem: mutação já invalidou
        void qc.invalidateQueries({ queryKey: THREADS_KEY });
        void qc.invalidateQueries({ queryKey: UNREAD_KEY });
        void qc.invalidateQueries({ queryKey: threadKey(threadId) });
      },
    );

    const offThreadCreated = subscribe<DirectThreadCreatedPayload>(
      WS_EVENTS.DIRECT_THREAD_CREATED,
      (envelope: WsEventEnvelope<DirectThreadCreatedPayload>) => {
        if (envelope.data.createdByUserId === currentUserId) return; // própria iniciação
        void qc.invalidateQueries({ queryKey: THREADS_KEY });
        void qc.invalidateQueries({ queryKey: UNREAD_KEY });
      },
    );

    const offThreadRead = subscribe<DirectThreadReadPayload>(
      WS_EVENTS.DIRECT_THREAD_READ,
      (envelope: WsEventEnvelope<DirectThreadReadPayload>) => {
        const { userId, threadId } = envelope.data;
        if (userId !== currentUserId) return; // só me importa quando EU li (ex.: outra aba)
        void qc.invalidateQueries({ queryKey: UNREAD_KEY });
        void qc.invalidateQueries({ queryKey: THREADS_KEY });
        void qc.invalidateQueries({ queryKey: threadKey(threadId) });
      },
    );

    return () => {
      offMessage();
      offThreadCreated();
      offThreadRead();
    };
  }, [qc, subscribe, canParticipate, currentUserId]);
}
